/**
 * viewportTiles — enumerate the {z,x,y} tile coords currently visible in a
 * MapLibre viewport, for a given source tileSize and zoom clamp.
 *
 * AK-193 (ADR-037 #6, audit F1): used by the adjacent-date prefetch effect to
 * warm exactly the tiles the user already sees, so a date-step renders warm
 * instead of cold (blank→pop). Pure web-mercator math — no MapLibre internals,
 * unit-testable with a plain bounds + zoom.
 *
 * tileSize semantics: a 512px source addresses tiles one zoom level coarser
 * than a 256px source, so the effective tile-grid zoom is
 * `floor(mapZoom) - log2(tileSize/256)` (i.e. -1 for 512). The result is then
 * clamped to [minzoom, maxzoom] so we never enumerate tiles the source would
 * overzoom/underzoom rather than fetch.
 */

export interface LngLatBoundsLike {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface TileCoord {
  z: number;
  x: number;
  y: number;
}

function lng2tile(lng: number, z: number): number {
  return Math.floor(((lng + 180) / 360) * 2 ** z);
}

function lat2tile(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** z,
  );
}

/**
 * @param bounds   viewport bounds in lng/lat
 * @param mapZoom  the map's current (fractional) zoom
 * @param tileSize source tile pixel size (256 or 512)
 * @param minzoom  source minzoom clamp
 * @param maxzoom  source maxzoom clamp
 * @param cap      max tiles to return (guards against a huge low-zoom viewport
 *                 firing hundreds of prefetches). Default 24 — comfortably
 *                 covers a desktop viewport at the typical farm zoom.
 */
export function viewportTiles(
  bounds: LngLatBoundsLike,
  mapZoom: number,
  tileSize: 256 | 512,
  minzoom: number,
  maxzoom: number,
  cap = 24,
): TileCoord[] {
  // 512px tiles are one zoom coarser than the map's display zoom.
  const sizeOffset = Math.log2(tileSize / 256); // 0 for 256, 1 for 512
  let z = Math.floor(mapZoom) - sizeOffset;
  z = Math.max(minzoom, Math.min(maxzoom, z));
  if (z < 0) return [];

  const n = 2 ** z;
  const clamp = (v: number) => Math.max(0, Math.min(n - 1, v));
  const xMin = clamp(lng2tile(bounds.west, z));
  const xMax = clamp(lng2tile(bounds.east, z));
  // y grows southward; north has the smaller y.
  const yMin = clamp(lat2tile(bounds.north, z));
  const yMax = clamp(lat2tile(bounds.south, z));

  const out: TileCoord[] = [];
  for (let x = xMin; x <= xMax; x++) {
    for (let y = yMin; y <= yMax; y++) {
      out.push({ z, x, y });
      if (out.length >= cap) return out;
    }
  }
  return out;
}
